// ===== RubriqueForm.js =====
import React, { useState, useEffect } from 'react';

const RubriqueForm = ({ rubrique, classes, onSave, onCancel, loading }) => {
  const [formData, setFormData] = useState({
    code: '',
    libelle: '',
    classe_id: '',
    description: ''
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (rubrique) {
      setFormData({
        code: rubrique.code || '',
        libelle: rubrique.libelle || '',
        classe_id: rubrique.classe_id?._id || '',
        description: rubrique.description || ''
      });
    }
  }, [rubrique]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.code.trim()) {
      newErrors.code = 'Le code est obligatoire';
    } else if (!/^[1-9]\d$/.test(formData.code)) {
      newErrors.code = 'Le code doit être de 2 chiffres (ex: 10, 11, 12...)';
    }

    if (!formData.libelle.trim()) {
      newErrors.libelle = 'Le libellé est obligatoire';
    } else if (formData.libelle.length > 200) {
      newErrors.libelle = 'Le libellé ne peut pas dépasser 200 caractères';
    }

    if (!formData.classe_id) {
      newErrors.classe_id = 'La classe est obligatoire';
    }

    // Vérifier que le code commence par le code de la classe sélectionnée
    if (formData.code && formData.classe_id) {
      const selectedClasse = classes.find(c => c._id === formData.classe_id);
      if (selectedClasse && !formData.code.startsWith(selectedClasse.code)) {
        newErrors.code = `Le code doit commencer par ${selectedClasse.code}`;
      }
    }

    if (formData.description && formData.description.length > 500) {
      newErrors.description = 'La description ne peut pas dépasser 500 caractères';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSave(formData);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Effacer l'erreur du champ modifié
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleClasseChange = (e) => {
    const selectedClasseId = e.target.value;
    const selectedClasse = classes.find(c => c._id === selectedClasseId);
    
    setFormData(prev => ({
      ...prev,
      classe_id: selectedClasseId,
      // Si c'est une nouvelle rubrique, préfixer le code avec le code de classe
      code: !rubrique && selectedClasse ? selectedClasse.code + (prev.code.length > 1 ? prev.code.substring(1) : '0') : prev.code
    }));
    
    if (errors.classe_id) {
      setErrors(prev => ({
        ...prev,
        classe_id: ''
      }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Classe */}
      <div>
        <label htmlFor="classe_id" className="block text-sm font-medium text-gray-700">
          Classe *
        </label>
        <select
          id="classe_id"
          name="classe_id"
          value={formData.classe_id}
          onChange={handleClasseChange}
          disabled={!!rubrique} // La classe ne peut pas être modifiée
          className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 ${
            errors.classe_id ? 'border-red-300' : ''
          } ${rubrique ? 'bg-gray-100' : ''}`}
        >
          <option value="">Sélectionner une classe...</option>
          {classes.map(classe => (
            <option key={classe._id} value={classe._id}>
              Classe {classe.code} - {classe.libelle}
            </option>
          ))}
        </select>
        {errors.classe_id && <p className="mt-1 text-sm text-red-600">{errors.classe_id}</p>}
        {rubrique && (
          <p className="mt-1 text-sm text-gray-500">La classe ne peut pas être modifiée</p>
        )}
      </div>

      {/* Code */}
      <div>
        <label htmlFor="code" className="block text-sm font-medium text-gray-700">
          Code rubrique *
        </label>
        <input
          type="text"
          id="code"
          name="code"
          value={formData.code}
          onChange={handleChange}
          disabled={!!rubrique} // Le code ne peut pas être modifié
          className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 ${
            errors.code ? 'border-red-300' : ''
          } ${rubrique ? 'bg-gray-100' : ''}`}
          placeholder="Ex: 10, 11, 12..."
          maxLength={2}
        />
        {errors.code && <p className="mt-1 text-sm text-red-600">{errors.code}</p>}
        {rubrique && (
          <p className="mt-1 text-sm text-gray-500">Le code ne peut pas être modifié</p>
        )}
      </div>

      {/* Libellé */}
      <div>
        <label htmlFor="libelle" className="block text-sm font-medium text-gray-700">
          Libellé *
        </label>
        <input
          type="text"
          id="libelle"
          name="libelle"
          value={formData.libelle}
          onChange={handleChange}
          className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 ${
            errors.libelle ? 'border-red-300' : ''
          }`}
          placeholder="Ex: Capital"
          maxLength={200}
        />
        {errors.libelle && <p className="mt-1 text-sm text-red-600">{errors.libelle}</p>}
        <p className="mt-1 text-sm text-gray-500">
          {formData.libelle.length}/200 caractères
        </p>
      </div>

      {/* Description */}
      <div>
        <label htmlFor="description" className="block text-sm font-medium text-gray-700">
          Description
        </label>
        <textarea
          id="description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          rows={3}
          className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 ${
            errors.description ? 'border-red-300' : ''
          }`}
          placeholder="Description optionnelle de la rubrique..."
          maxLength={500}
        />
        {errors.description && <p className="mt-1 text-sm text-red-600">{errors.description}</p>}
        <p className="mt-1 text-sm text-gray-500">
          {formData.description.length}/500 caractères
        </p>
      </div>

      {/* Boutons */}
      <div className="flex justify-end space-x-3 pt-4">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          disabled={loading}
        >
          Annuler
        </button>
        <button
          type="submit"
          disabled={loading}
          className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Enregistrement...' : rubrique ? 'Modifier' : 'Créer'}
        </button>
      </div>
    </form>
  );
};

// Exports

export default RubriqueForm;